import { createContext, PropsWithChildren, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import AuthContext from "../../components/AuthContext";
import { IsDesignMode } from "../Utils/runtimeDetection";

type DesignerLiveChatState = { enabled: boolean; signal?: AbortSignal; canUse(): boolean };

const DesignerLiveChatContext = createContext<DesignerLiveChatState>({ enabled: false, canUse: () => false });
export const useDesignerLiveChat = () => useContext(DesignerLiveChatContext);

/** Designer Chat follows runtime authentication; retained handles cannot cross account lifetimes. */
export function DesignerLiveChatProvider({ children }: PropsWithChildren) {
  const auth = useContext(AuthContext);
  const identity = IsDesignMode && auth.isAuthenticated
    ? (auth.account?.homeAccountId ?? auth.account?.localAccountId ?? auth.account?.username ?? "authenticated")
    : undefined;
  // StrictMode replays cleanup/setup without recreating memoized controllers.
  const [generation, renewLifetime] = useState(0);
  // Each runtime identity needs a fresh cancellation lifetime.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const controller = useMemo(() => new AbortController(), [identity, generation]);
  const currentIdentity = useRef(identity);
  currentIdentity.current = identity;
  const canUse = useCallback(() => !!identity && currentIdentity.current === identity && !controller.signal.aborted, [controller, identity]);
  useEffect(() => {
    if (!identity) controller.abort();
    else if (controller.signal.aborted) renewLifetime((value) => value + 1);
    return () => controller.abort();
  }, [controller, identity]);
  return (
    <DesignerLiveChatContext.Provider value={{ enabled: !!identity, signal: controller.signal, canUse }}>{children}</DesignerLiveChatContext.Provider>
  );
}
